import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FileIcon as FilePdf,
  Eye,
  Handshake,
  FileUser,
  Layers
} from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  CircularProgress
} from "@mui/material";
import { PdfViewerDialog } from "../PDFViewer/PdfViewerDialog";
import EnhancedPdfViewer from "../PDFViewer/EnhancedPdfViewer.new";
import ReactJson from "react-json-view";
import fetchData from "../../utils/fetchData";
import DOMPurify from "dompurify";
import CloseIcon from "@mui/icons-material/Close";
import { setSelectedIndex, setAttachments } from "../../store/ApplicationSlice";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PdfJsonViewer from "../PDFViewer/PDFViewerNew";
import { API_BASE_URL } from "../../config/config";

export function DocumentsSection ({ username, ...props }) {
  const dispatch = useDispatch();
  const selectedApplicationId = useSelector(
    state => state.intakeData.selectedAppId
  );
  const selectedApplicationData = useSelector(
    state => state.intakeData.selectedApplicationData
  );
  const comments = useSelector(state => state.intakeData.comments);
  const attachments = useSelector(state => state.intakeData.attachments);
  const selectedIndex = useSelector(state => state.intakeData.selectedIndex);
  const [loading, setLoading] = useState(false);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [openEnhancedPdfDialog, setOpenEnhancedPdfDialog] = useState(false);
  const [openJsonDialog, setOpenJsonDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentPdfLink, setCurrentPdfLink] = useState("");
  const [pdfName, setPdfName] = useState("");
  const [attachmentJsonData, setAttachmentJsonData] = useState({});
  const [warningMessage, setWarningMessage] = useState("");

  // Add state for react-pdf
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    if (selectedApplicationData?.application_id) {
      setLoading(true);
      fetchData(
        `${API_BASE_URL}/insureai/ml/intake/applications/v2/attachments?application_id=${selectedApplicationData.application_id}`,
        "GET"
      )
        .then(data => {
          dispatch(setAttachments(data));
          dispatch(setSelectedIndex(0)); // reset to first document when new data arrives
        })
        .catch(error => console.error("Error fetching attachments:", error))
        .finally(() => setLoading(false));
    } else {
      dispatch(setAttachments([]));
    }
  }, [selectedApplicationData]);

  const handleOpenExtraction = async () => {
    if (!selectedAttachment?.doc_id) return;

    try {
      // Open extraction viewer in new tab with doc_id parameter
      const extractionUrl = `/app/extraction-viewer?docId=${
        selectedAttachment.doc_id
      }&pdfUrl=${encodeURIComponent(
        selectedAttachment.link
      )}&docName=${encodeURIComponent(selectedAttachment.name)}`;
      window.open(extractionUrl, "_blank");
    } catch (error) {
      console.error("Error opening extraction viewer:", error);
    }
  };

  const handleViewPDF = (link, name, useEnhancedViewer = false) => {
    setCurrentPdfLink(link);
    setPdfName(name);
    setAttachmentJsonData({});
    if (useEnhancedViewer) {
      setOpenEnhancedPdfDialog(true);
    } else {
      setOpenPdfDialog(true);
    }
  };

  // Note: JSON viewing functionality is currently disabled in the UI
  /*
  const handleViewJSON = (docId, name) => {
    setPdfName(name);
    fetchData(
      `https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/detailed_json?doc_id=${docId}`,
      "GET"
    )
      .then(data => {
        setAttachmentJsonData(data?.[0]?.json || data);
        setOpenJsonDialog(true);
      })
      .catch(error => console.error("Error fetching JSON:", error));
  };
  */

  const handleEditButton = () => {
    setOpenEditDialog(true);
    setOpenPdfDialog(false);
    setOpenEnhancedPdfDialog(false);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
  };

  const closeDialog = () => {
    setOpenPdfDialog(false);
    setOpenEnhancedPdfDialog(false);
    setOpenEditDialog(false);
    setOpenJsonDialog(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px"
        }}
      >
        <CircularProgress sx={{ color: "#ea580c" }} />
      </Box>
    );
  }

  // Use the currently selected attachment or an empty object
  const selectedAttachment = attachments[selectedIndex] || {};

  return (
    <div
      data-section={`${props["data-section"]}-${selectedAttachment?.doc_id}-${selectedAttachment?.name}`}
    >
      <Card className="p-5 bg-white shadow-md">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-base font-semibold flex items-center">
            <FilePdf className="h-4 w-4 mr-2 text-[#FF5533]" />
            Application Documents
          </h3>
          {/* <div className='flex gap-2'>
            <Button variant='outline' size='sm'>
              <Filter className='h-4 w-4 mr-2' />
              Filter
            </Button>
            <Button variant='outline' size='sm'>
              <Download className='h-4 w-4 mr-2' />
              Export All
            </Button>
          </div> */}
        </div>
        <div className="grid grid-cols-3 gap-6">
          {/* Left Container: Selected Document Summary */}
          <div className="col-span-2">
            {selectedAttachment && (
              <div className="bg-gray-100 rounded-md relative">
                {/* Sticky header */}
                <div className="sticky top-0 z-10 bg-gray-50 p-4 border-b border-gray-200 rounded-t-md">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold flex items-center gap-2">
                      {selectedAttachment.name}
                      <div className="relative group">
                        {selectedAttachment.is_application ? (
                          <Handshake className="h-5 w-5 text-orange-500" />
                        ) : (
                          <FileUser className="h-5 w-5 text-orange-500" />
                        )}
                        <div className="absolute hidden group-hover:block bg-gray-800 text-white text-xs py-1 px-2 rounded -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                          {selectedAttachment.is_application
                            ? "Accord Application"
                            : "Non-Accord Application"}
                        </div>
                      </div>
                    </h4>
                    <div className="flex gap-2">
                      {/* <Button
                        variant="outline"
                        size="sm"
                        className="px-3 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:text-blue-800 !h-7"
                        onClick={() =>
                          handleViewPDF(
                            selectedAttachment.link,
                            selectedAttachment.name
                          )
                        }
                      >
                        <FilePdf className="h-3.5 w-3.5 mr-1.5" />
                        View PDF
                      </Button> */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-3 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:text-blue-800 !h-7"
                        onClick={() =>
                          handleViewPDF(
                            selectedAttachment.link,
                            selectedAttachment.name,
                            true
                          )
                        }
                      >
                        <FilePdf className="h-3.5 w-3.5 mr-1" />
                        View PDF
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-3 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:text-blue-800 !h-7"
                        onClick={handleOpenExtraction}
                      >
                        <Layers className="h-3.5 w-3.5 mr-1" />
                        View Extraction
                      </Button>
                    </div>
                  </div>
                </div>
                {/* Scrollable content */}
                <div className="p-4 max-h-[300px] overflow-y-auto">
                  <div
                    className=" text-gray-700"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        selectedAttachment.summary || ""
                      )
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          {/* Right Container: Document List */}
          <div>
            <Card className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold">Document List</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose between classic or enhanced viewer with annotation
                    support
                  </p>
                </div>
              </div>
              <div className="space-y-2 max-h-[230px] overflow-y-auto">
                {attachments.map((doc, index) => (
                  <div
                    key={index}
                    onClick={() => dispatch(setSelectedIndex(index))}
                    className={`border rounded-md shadow-md cursor-pointer ${
                      selectedIndex === index
                        ? "bg-[#FFF0ED] border-[#FFDED6]"
                        : "bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between p-2">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <FilePdf className="h-4 w-4 text-[#FF5533] flex-shrink-0" />
                        <div className="overflow-hidden">
                          <h4 className="text-sm font-medium truncate">
                            {doc.name}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {doc.pages ? `${doc.pages} pages` : ""}
                          </p>
                        </div>
                      </div>
                      <button
                        className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                        onClick={e => {
                          e.stopPropagation(); // Prevent triggering the parent's onClick
                          dispatch(setSelectedIndex(index));
                          handleViewPDF(doc.link, doc.name, true);
                        }}
                        title="View PDF"
                      >
                        <Eye className="h-4 w-4 text-gray-500 hover:text-[#FF5533]" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </Card>{" "}
      {/* Classic PDF Viewer Dialog */}
      <PdfViewerDialog
        open={openPdfDialog}
        onClose={() => setOpenPdfDialog(false)}
        pdfName={pdfName}
        currentPdfLink={currentPdfLink}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        numPages={numPages}
        setNumPages={setNumPages}
        selectedAttachment={selectedAttachment}
        handleEditButton={handleEditButton}
      />
      {/* Enhanced PDF Viewer Dialog */}
      <EnhancedPdfViewer
        open={openEnhancedPdfDialog}
        onClose={() => setOpenEnhancedPdfDialog(false)}
        pdfName={pdfName}
        currentPdfLink={currentPdfLink}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        numPages={numPages}
        setNumPages={setNumPages}
        selectedAttachment={selectedAttachment}
        handleEditButton={handleEditButton}
        dataSection={`${selectedApplicationId}-${props["data-section"]}-${selectedAttachment?.doc_id}-${selectedAttachment?.name}`}
        username={username}
      />
      {/* JSON Viewer Dialog */}
      <Dialog
        open={openJsonDialog}
        onClose={() => setOpenJsonDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {pdfName} JSON Data
          <span
            onClick={() => setOpenJsonDialog(false)}
            style={{ cursor: "pointer", float: "right" }}
          >
            X
          </span>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: "65vh", overflowY: "auto" }}>
            <ReactJson
              src={attachmentJsonData || {}}
              theme="monokai"
              style={{ fontSize: "14px" }}
              displayObjectSize={false}
              displayDataTypes={false}
            />
          </Box>
        </DialogContent>
      </Dialog>
      {/* Edit PDF Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleEditDialogClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            border: "1px solid #ea580c",
            height: "95%",
            width: "80%",
            maxWidth: "none",
            maxHeight: "none"
          }
        }}
      >
        <DialogTitle style={{ color: "#ea580c", paddingBottom: "10px" }}>
          {pdfName}
          <span
            onClick={handleEditDialogClose}
            className="close-icon"
            style={{ cursor: "pointer", float: "right" }}
          >
            <CloseIcon />
          </span>
        </DialogTitle>
        <DialogContent>
          {warningMessage ? (
            <>
              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  backgroundColor: "#ea580c26",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center"
                }}
              ></Box>
              <WarningAmberOutlinedIcon
                sx={{ color: "#ea580c", marginRight: "8px" }}
              />
              <p style={{ margin: 0, color: "#ea580c" }}>{warningMessage}</p>
            </>
          ) : (
            <Box
              sx={{
                mb: 1,
                backgroundColor: "#e3f2fd",
                borderRadius: "4px",
                display: "none",
                alignItems: "center",
                padding: "6px"
              }}
            >
              <InfoOutlinedIcon sx={{ color: "#0d47a1", marginRight: "8px" }} />
              <p style={{ margin: 0, color: "#0d47a1", fontSize: "14px" }}>
                Review the document carefully before editing. Click on yellow
                areas on the PDF to update data in the highlighted panel.
              </p>
            </Box>
          )}
          <Box sx={{ height: "65vh" }}>
            <PdfJsonViewer
              docId={selectedAttachment.doc_id}
              applicationId={selectedApplicationData?.application_id}
              currentPdfLink={currentPdfLink}
              closeDialog={closeDialog}
              setWarningMessage={setWarningMessage}
            />
          </Box>
        </DialogContent>
      </Dialog>
      {/* Remove Extraction Viewer Dialog - no longer needed */}
      {/* 
      <Dialog
        open={openExtractionDialog}
        onClose={() => setOpenExtractionDialog(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "95vw",
            height: "95vh",
            maxWidth: "none",
            maxHeight: "none"
          }
        }}
      >
        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          <ExtractionViewer
            pdfUrl={"/avant-ui/av-rich/accord.pdf"}
            extractionData={sampleJSON}
            onClose={() => setOpenExtractionDialog(false)}
          />
        </DialogContent>
      </Dialog>
      */}
      <style>{`
        .pdf-page {
          canvas {
            max-width: 100%;
            height: auto !important;
            background-color: white !important;
            image-rendering: high-quality;
            -webkit-font-smoothing: antialiased;
          }
        }
        .pdf-doc-wrapper {
          display: flex;
          justify-content: center;
          width: 100%;
          background-color: white;
        }
      `}</style>
    </div>
  );
}
